import { useState } from "react";
import { ExternalLink, Play } from "lucide-react";
import { youTubeWatchUrl } from "../resourcesData";

/**
 * Click-to-load: nothing is requested from YouTube until play is pressed.
 * A plain iframe, not react-player, which would need `script-src` widened in
 * public/_headers.
 *
 * "Open on YouTube" stays visible because districts block YouTube and a
 * blocked iframe still fires `onload` - it cannot be detected from JS.
 */
export default function VideoCard({ item }) {
  const [activated, setActivated] = useState(false);
  const { youTubeId, startSeconds, posterSrc, title, description } = item;

  const embedSrc =
    `https://www.youtube-nocookie.com/embed/${youTubeId}` +
    "?autoplay=1&rel=0&modestbranding=1&playsinline=1&color=white" +
    (startSeconds ? `&start=${startSeconds}` : "");

  return (
    <div className="flex h-full flex-col gap-2 rounded-lg border border-[#DDDDDD] bg-white p-4 transition hover:shadow-md">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <span className="w-fit rounded-full bg-[#EAF4FF] px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-[#3387CF]">
          Video
        </span>

        <a
          href={youTubeWatchUrl(youTubeId, startSeconds)}
          target="_blank"
          rel="noopener noreferrer"
          className="flex shrink-0 items-center gap-1 whitespace-nowrap text-xs font-semibold text-[#3387CF] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3387CF] focus-visible:ring-offset-2"
        >
          Open on YouTube
          <ExternalLink className="h-3.5 w-3.5" />
          <span className="sr-only">(opens in a new tab)</span>
        </a>
      </div>

      <div className="relative aspect-video w-full overflow-hidden rounded bg-black">
        {activated ? (
          <iframe
            className="absolute inset-0 h-full w-full border-0"
            src={embedSrc}
            title={title}
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        ) : (
          <button
            type="button"
            onClick={() => setActivated(true)}
            aria-label={`Play ${title}`}
            className="group absolute inset-0 h-full w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#3387CF]"
          >
            {/* Explicit h-full/w-full: src/index.css sets `img { max-width: unset }` */}
            <img
              src={posterSrc}
              alt=""
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <span className="absolute inset-0 bg-black/25 transition group-hover:bg-black/40" />
            <span className="absolute inset-0 flex items-center justify-center">
              <Play className="h-10 w-10 text-white drop-shadow-md" />
            </span>
          </button>
        )}
      </div>

      <p className="m-0 text-sm font-semibold text-[#1E2D3D] sm:text-base">
        {title}
      </p>
      {description && (
        <p className="m-0 text-sm text-[#555555] sm:text-base">{description}</p>
      )}
    </div>
  );
}
